"use client";

import { useState } from "react";
import { Header } from "@/components/ui/Header";
import { Footer } from "@/components/ui/Footer";
import { trackContactsFormSubmit } from "@/lib/metrics";
import { siteConfig } from "@/config/site";
import type { ScriptarioLocale } from "@/config/locales";

const IS_SCRIPTARIO_BUILD = process.env.NEXT_PUBLIC_SITE_ID === "scriptario";
const TELEGRAM_LABEL = decodeURIComponent("%54%65%6C%65%67%72%61%6D");

function getSupportLinkLabel(url: string) {
  if (!url.trim()) return "";
  if (/^https:\/\/wa\.me\//i.test(url)) return "WhatsApp";
  if (/^https:\/\/t\.me\//i.test(url)) return `Написать в ${TELEGRAM_LABEL}`;
  return url.replace(/^mailto:/, "");
}

const scriptarioContactsCopy: Record<ScriptarioLocale, {
  pageTitle: string;
  channelTitle: string;
  channelDescription: string;
  cardTitle: string;
  cardDescription: string;
  supportButton: string;
}> = {
  de: { pageTitle: "Kontakt", channelTitle: "WhatsApp", channelDescription: "Support, Kontozugang und Fragen zur Abrechnung", cardTitle: "Scriptario-Support kontaktieren", cardDescription: "Senden Sie Ihre Frage über WhatsApp an das Scriptario-Supportteam. Wir antworten in Ihrer Sprache.", supportButton: "WhatsApp öffnen" },
  en: {
    pageTitle: "Contact us",
    channelTitle: "WhatsApp",
    channelDescription: "Support, account access, and billing questions",
    cardTitle: "Message Scriptario support",
    cardDescription: "Send the Scriptario support team your question in WhatsApp. We will reply in the language you use.",
    supportButton: "Open WhatsApp",
  },
  es: {
    pageTitle: "Contacta con nosotros",
    channelTitle: "WhatsApp",
    channelDescription: "Soporte, acceso a la cuenta y preguntas de facturación",
    cardTitle: "Escribe al soporte de Scriptario en WhatsApp",
    cardDescription: "Envía tu pregunta al equipo de soporte de Scriptario en WhatsApp. Te responderemos en el idioma que utilices.",
    supportButton: "Abrir WhatsApp",
  },
  "pt-br": {
    pageTitle: "Fale conosco",
    channelTitle: "WhatsApp",
    channelDescription: "Suporte, acesso à conta e dúvidas de faturamento",
    cardTitle: "Fale com o suporte do Scriptario no WhatsApp",
    cardDescription: "Envie sua pergunta à equipe de suporte do Scriptario pelo WhatsApp. Responderemos no idioma que você usar.",
    supportButton: "Abrir WhatsApp",
  },
  it: {
    pageTitle: "Contattaci",
    channelTitle: "WhatsApp",
    channelDescription: "Supporto, accesso all'account e domande di fatturazione",
    cardTitle: "Scrivi al supporto Scriptario su WhatsApp",
    cardDescription: "Invia la tua domanda al team di supporto Scriptario su WhatsApp. Risponderemo nella lingua che usi.",
    supportButton: "Apri WhatsApp",
  },
};

function getScriptarioContactsCopy() {
  return scriptarioContactsCopy[siteConfig.locale === "ru" ? "en" : siteConfig.locale] ?? scriptarioContactsCopy.en;
}

function ContactForm() {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState<"" | "sending" | "sent" | "error">("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackContactsFormSubmit();
    setStatus("sending");
    try {
      const res = await fetch("/form-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, contact, comment }),
      });
      if (res.ok) { setStatus("sent"); } else { setStatus("error"); }
    } catch { setStatus("error"); }
  };

  const inputClass = "border border-[rgba(0,0,0,0.1)] flex items-start px-[16px] py-[10px] rounded-[8px] w-full bg-transparent outline-none [font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-[rgba(0,0,0,0.9)] placeholder:text-[rgba(0,0,0,0.4)] placeholder:tracking-[-0.28px]";

  return (
    <div className="bg-white flex flex-col gap-[24px] items-center p-[20px] rounded-[24px] w-full max-w-[616px]">
      <div className="flex flex-col gap-[8px] items-center w-full">
        <p
          className="[font-family:var(--font-commissioner)] font-semibold not-italic text-[24px] leading-[32px] text-[rgba(0,0,0,0.9)] text-center w-full"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          Получите 500 минут бесплатно
        </p>
        <p
          className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-[rgba(0,0,0,0.9)] text-center w-full"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          Поделитесь своим мнением, идеями и пожеланиями по развитию сервиса — в знак благодарности мы подарим вам минуты для использования.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-[16px] w-full">
        <div className="flex gap-[16px] max-tablet:flex-col">
          <label className="flex flex-col gap-[8px] flex-1">
            <span
              className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-[rgba(0,0,0,0.9)]"
              style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
            >
              Имя
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Введите имя"
              className={inputClass}
              style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
            />
          </label>
          <label className="flex flex-col gap-[8px] flex-1">
            <span
              className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-[rgba(0,0,0,0.9)]"
              style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
            >
              Телефон или телеграм
            </span>
            <input
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Укажите номер или телеграм"
              className={inputClass}
              style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
            />
          </label>
        </div>

        <label className="flex flex-col gap-[8px] flex-1">
          <span
            className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-[rgba(0,0,0,0.9)]"
            style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
          >
            Комментарий
          </span>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Введите комментарий"
            className={`${inputClass} resize-none h-[100px]`}
            style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
          />
        </label>

        <button
          type="submit"
          disabled={status === "sending" || status === "sent"}
          className="bg-[var(--brand-primary)] flex gap-[8px] items-center justify-center px-[16px] py-[10px] rounded-[12px] w-full cursor-pointer border-none disabled:opacity-50 disabled:cursor-default"
        >
          <span
            className="[font-family:var(--font-commissioner)] font-medium not-italic text-[16px] leading-[20px] text-white whitespace-nowrap"
            style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
          >
            {status === "sending" ? "Отправка..." : status === "sent" ? "Отправлено ✓" : status === "error" ? "Ошибка — попробуйте ещё раз" : "Отправить"}
          </span>
        </button>
      </form>
    </div>
  );
}

function ScriptarioSupportCard() {
  const copy = getScriptarioContactsCopy();

  return (
    <div className="bg-white flex flex-col gap-[24px] items-center p-[20px] rounded-[24px] w-full max-w-[616px]">
      <div className="flex flex-col gap-[8px] items-center w-full">
        <p
          className="[font-family:var(--font-commissioner)] font-semibold not-italic text-[24px] leading-[32px] text-[rgba(0,0,0,0.9)] text-center w-full"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          {copy.cardTitle}
        </p>
        <p
          className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[18px] text-[rgba(0,0,0,0.9)] text-center w-full"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          {copy.cardDescription}
        </p>
      </div>

      <a
        href={siteConfig.supportUrl}
        className="bg-[var(--brand-primary)] flex gap-[8px] items-center justify-center px-[16px] py-[10px] rounded-[12px] w-full cursor-pointer border-none"
      >
        <span
          className="[font-family:var(--font-commissioner)] font-medium not-italic text-[16px] leading-[20px] text-white whitespace-nowrap"
          style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
        >
          {copy.supportButton}
        </span>
      </a>
    </div>
  );
}

export function ContactsPageClient() {
  const scriptarioCopy = getScriptarioContactsCopy();
  const hasSupportUrl = siteConfig.supportUrl.trim().length > 0;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="relative flex-1 flex justify-center px-[16px] pt-[80px] pb-[80px] overflow-hidden">
        {/* Background ellipse */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="-translate-x-1/2 -translate-y-1/2 absolute h-[1047px] left-[calc(66.67%-4.5px)] top-[calc(50%-147.5px)] w-[1077px]">
            <svg viewBox="0 0 1077 1047" fill="none" className="size-full">
              <ellipse cx="538" cy="523" rx="538" ry="523" fill="url(#contactsGrad)" />
              <defs>
                <radialGradient id="contactsGrad" cx="50%" cy="50%">
                  <stop offset="0%" stopColor="var(--brand-primary-soft)" />
                  <stop offset="100%" stopColor="white" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>

        <div className="relative flex gap-[16px] w-full max-w-[1248px] max-desktop:flex-col max-desktop:items-stretch max-desktop:gap-[40px]">
          {/* Left column — title + contacts */}
          <div className="flex flex-col gap-[40px] w-[616px] max-desktop:w-full">
            <h1
              className="[font-family:var(--font-commissioner)] font-extrabold not-italic text-[48px] leading-[48px] text-[rgba(0,0,0,0.9)] tracking-[-0.96px] w-full max-tablet:text-[32px] max-tablet:leading-[36px] max-tablet:tracking-[-0.64px]"
              style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0', fontWeight: 800 }}
            >
              {IS_SCRIPTARIO_BUILD ? scriptarioCopy.pageTitle : "Свяжитесь с нами любым удобным способом"}
            </h1>

            <div className="flex flex-col gap-[24px]">
              <div className="flex flex-col gap-[8px]">
                <h2
                  className="[font-family:var(--font-commissioner)] font-semibold not-italic text-[18px] leading-[24px] text-[rgba(0,0,0,0.9)]"
                  style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
                >
                  {IS_SCRIPTARIO_BUILD ? scriptarioCopy.channelTitle : ["Tele", "gram"].join("")}
                </h2>
                <p
                  className="[font-family:var(--font-commissioner)] font-normal not-italic text-[18px] leading-[24px] text-black tracking-[-0.36px]"
                  style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
                >
                  {IS_SCRIPTARIO_BUILD ? (
                    <>
                      {scriptarioCopy.channelDescription}<br />
                      <a
                        href={siteConfig.supportUrl}
                        style={{ color: "var(--brand-primary)" }}
                      >
                        {getSupportLinkLabel(siteConfig.supportUrl)}
                      </a>
                    </>
                  ) : (
                    <>
                      Быстрые ответы на любые вопросы<br />
                      {hasSupportUrl ? (
                        <a href={siteConfig.supportUrl} style={{ color: "var(--brand-primary)" }}>{getSupportLinkLabel(siteConfig.supportUrl)}</a>
                      ) : (
                        <span style={{ color: "var(--brand-primary)" }}>{siteConfig.supportEmail}</span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Right column — form */}
          {IS_SCRIPTARIO_BUILD ? <ScriptarioSupportCard /> : <ContactForm />}
        </div>
      </main>

      <Footer />
    </div>
  );
}
